import { useSyncExternalStore } from 'react';

/**
 * Folds people choose for more room, remembered on this device: the menu folded to a rail of icons
 * (Teampace 2965), and the call card folded to one slim bar while on a call (Teampace 2972).
 * Kept in the browser's storage when it can be; without it (a private window, blocked storage) the
 * fold still works for this visit and starts open next time.
 */

/** The menu on the left, folded to its icons. */
export const MENU_FOLD = 'stitch.menu.folded';
/** The call card, folded to one bar. */
export const CALL_FOLD = 'stitch.call.folded';

/** Where a fold is kept: the browser's localStorage, or a stand-in (tests). */
export interface FoldStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** The browser's storage, or null where there's none (or it can't be reached). */
function deviceStorage(): FoldStorage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** Whether `key` is folded on this device: only a saved "1" is; anything else (or nothing) is open. */
export function readFold(key: string, storage: FoldStorage | null = deviceStorage()): boolean {
  try {
    return storage?.getItem(key) === '1';
  } catch {
    return false;
  }
}

/** Remembers the fold on this device; quietly does nothing where storage is blocked or full. */
export function saveFold(key: string, folded: boolean, storage: FoldStorage | null = deviceStorage()): void {
  try {
    storage?.setItem(key, folded ? '1' : '0');
  } catch {
    // Private mode or storage full: the fold still works for this visit.
  }
}

// Each fold's value for this visit (read once from storage), shared by everything that shows it.
const values = new Map<string, boolean>();
const listeners = new Map<string, Set<() => void>>();

function current(key: string): boolean {
  let v = values.get(key);
  if (v === undefined) {
    v = readFold(key);
    values.set(key, v);
  }
  return v;
}

/** Folds (or opens) `key` everywhere it shows, and remembers it on this device. */
export function setFold(key: string, folded: boolean): void {
  if (current(key) === folded) return;
  values.set(key, folded);
  saveFold(key, folded);
  for (const l of listeners.get(key) ?? []) l();
}

// One subscribe function per fold, so React keeps its subscription from render to render.
const subscribers = new Map<string, (on: () => void) => () => void>();
function subscriber(key: string): (on: () => void) => () => void {
  let sub = subscribers.get(key);
  if (!sub) {
    sub = (on) => {
      const set = listeners.get(key) ?? new Set();
      listeners.set(key, set);
      set.add(on);
      return () => set.delete(on);
    };
    subscribers.set(key, sub);
  }
  return sub;
}

/** A fold remembered on this device: [folded, set]. */
export function useFold(key: string): [boolean, (folded: boolean) => void] {
  const folded = useSyncExternalStore(
    subscriber(key),
    () => current(key),
    () => false
  );
  return [folded, (v) => setFold(key, v)];
}

/** What the folded call bar is named for a screen reader: "Call with (800) 692-7753, 15:54, muted, folded". */
export function callBarLabel(p: { who: string; clock: string; muted?: boolean; held?: boolean; waiting?: number }): string {
  const parts = [`Call with ${p.who}`];
  if (p.clock) parts.push(p.clock);
  if (p.muted) parts.push('muted');
  if (p.held) parts.push('on hold');
  if (p.waiting) parts.push(`${p.waiting} more ${p.waiting === 1 ? 'call' : 'calls'} waiting`);
  parts.push('folded');
  return parts.join(', ');
}

/**
 * Whether this device's call shows as the folded bar: only when the person folded it, the call is
 * connected, and nothing on the card needs them (a transfer under way, a click to hear the call, a
 * microphone that isn't working). A call ringing in has its own card and is never folded.
 */
export function showCallBar(p: { folded: boolean; connected: boolean; transferring?: boolean; needsClick?: boolean; micProblem?: boolean }): boolean {
  return p.folded && p.connected && !p.transferring && !p.needsClick && !p.micProblem;
}
